import { Component, OnDestroy, Renderer2, computed, effect, input, signal, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

import { ApDarkmodeModule, ApDarkmodeService } from '@ap/darkmode';
import { ApThemeService } from '@ap/theme';
import { ApInlineSvgDirective } from '@ap/inline-svg';

import { availableIllustrations } from './illustration.constant';

/** Injects an illustration svg */
@Component({
    selector: 'ap-illustration',
    templateUrl: './illustration.component.html',
    providers: [ApThemeService],
    host: {
        '[attr.aria-hidden]': 'hideTitle()' // eslint-disable-line @typescript-eslint/naming-convention
    },
    imports: [ApInlineSvgDirective, ApDarkmodeModule]
})
export class ApIllustrationComponent implements OnDestroy {
    private readonly themeService = inject(ApThemeService);
    private readonly renderer = inject(Renderer2);
    private readonly darkmodeService = inject(ApDarkmodeService);

    /**
     * Required - Name of the illustration to show
     */
    public readonly name = input.required<string>();

    /**
     * Width of the illustration
     */
    public readonly width = input<string>();

    /**
     * Height of the illustration
     */
    public readonly height = input<string>();

    /**
     * If you need to force light/dark mode
     */
    public readonly forceDarkmodeSetting = input<'light' | 'dark'>();

    /** Set to true when the image is decorative and should not be read by a screenreader */
    public readonly hideTitle = input(true);

    public readonly size = computed(() => {
        const width = this.width();
        const height = this.height();
        return width && height ? { width, height } : {};
    });

    public readonly darkmodeSetting = signal<'light' | 'dark'>('light');
    private readonly destroy$ = new Subject<void>();

    constructor() {
        effect(() => {
            const forceDarkmodeSetting = this.forceDarkmodeSetting();
            if (!!forceDarkmodeSetting) {
                this.darkmodeSetting.set(forceDarkmodeSetting);
            } else {
                // We subscribe to the darkmodeSetting because we need to be able to switch the illustration if possible and available
                this.darkmodeService
                    .darkmodeSetting()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((setting) => {
                        this.darkmodeSetting.set(setting);
                    });
            }
        });
    }

    /**
     * Set correct a11y attributes after inserting SVG
     */
    protected onSvgInserted(svg: SVGElement): void {
        if (this.hideTitle()) {
            this.renderer.setAttribute(svg, 'focusable', 'false');
            this.renderer.setAttribute(svg, 'role', 'presentation');
        } else {
            this.renderer.setAttribute(svg, 'focusable', 'true');
        }
    }

    public getAssetUrl(): string {
        const staticUrl = this.themeService.getStaticUrl();
        const theme = this.themeService.getTheme();

        const lightAssetUrl = `${staticUrl}/illustration/${this.name()}.svg`;

        const darkAssetUrl =
            availableIllustrations[theme] && availableIllustrations[theme].indexOf(`${this.name()}-dark`) >= 0
                ? `${staticUrl}/illustration/${this.name()}-dark.svg`
                : lightAssetUrl;

        return this.darkmodeSetting() === 'dark' ? darkAssetUrl : lightAssetUrl;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
